/**
 * TractFilterPanel.tsx — T-012/W4 tract tier + type filter body.
 *
 * Pure presentational content rendered inside the LayersControl "Tracts ▾"
 * popover. Two sections:
 *   1. Opportunity Score Tier — 5 tiers (Prime…Low) mapped to TRACT_TIERS ids.
 *   2. Location Classification — 5 dominant_type values (TRACT_TYPES).
 *
 * Each row is an accessible toggle <button> with aria-pressed reflecting whether
 * the tier/type is currently in the filter (i.e. shown on the map). Swatch colours
 * match the choropleth tiers so the legend and filter read consistently.
 *
 * Kept Radix-free and parameterised so it can be unit-tested standalone (Radix
 * Popover content does not open reliably in jsdom — see MetricInfoButton.test.tsx).
 */
import { cn } from '@/lib/utils'
import { TRACT_TIERS, TRACT_TYPES } from './TractLayer'
import { DOMINANT_TYPE_LABELS } from '@/lib/tractModel'

export interface TractFilterPanelProps {
  /** Tier ids currently shown (subset of TRACT_TIERS). */
  tierFilter: string[]
  /** dominant_type values currently shown (subset of TRACT_TYPES). */
  typeFilter: string[]
  onTierToggle: (tier: string) => void
  onTypeToggle: (type: string) => void
  /**
   * T-426: the filter is applied with map.setFilter on the TRACT layers, which are only mounted
   * at the tract tier (z ≥ 9). Zoomed out onto the county/state choropleth there is no layer for
   * it to act on, and every click was silently discarded (Noreen, 2026-07-22). Set this at those
   * zooms so the rows go inert AND say why, rather than accepting input they cannot honour.
   */
  disabled?: boolean
}

// Tier id → display label + swatch. Swatches mirror the choropleth fill stops.
const TIER_ROWS: { id: (typeof TRACT_TIERS)[number]; label: string; color: string }[] = [
  { id: 'high', label: 'Prime (≥70)', color: '#15803d' },
  { id: 'good', label: 'Strong (≥60)', color: '#4ade80' },
  { id: 'moderate', label: 'Moderate (≥45)', color: '#f59e0b' },
  { id: 'low', label: 'Marginal (≥35)', color: '#f97316' },
  { id: 'poor', label: 'Low (<35)', color: '#ef4444' },
]

// dominant_type value → human-friendly label. Labels come from the shared
// DOMINANT_TYPE_LABELS map (single source of truth, also used by the score panel
// and drill-down) so the filter and the panels never drift apart.
const TYPE_ROWS: { id: (typeof TRACT_TYPES)[number]; label: string }[] = TRACT_TYPES.map((id) => ({
  id,
  label: DOMINANT_TYPE_LABELS[id] ?? id,
}))

interface FilterRowProps {
  label: string
  checked: boolean
  onToggle: () => void
  color?: string
  disabled?: boolean
}

function FilterRow({ label, checked, onToggle, color, disabled }: FilterRowProps) {
  return (
    <button
      onClick={onToggle}
      aria-pressed={checked}
      disabled={disabled}
      className={cn(
        `flex w-full items-center gap-2 rounded px-1 py-1
         focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
         transition-colors text-left`,
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-black/5 dark:hover:bg-white/5',
      )}
    >
      {/* Checkbox-style indicator */}
      <span
        aria-hidden="true"
        className={cn(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border transition-colors',
          checked
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-transparent',
        )}
      >
        {checked && (
          <svg
            viewBox="0 0 12 12"
            className="h-2.5 w-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {color && (
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      <span
        className={cn(
          'flex-1 text-[0.6875rem] transition-colors',
          checked ? 'text-foreground' : 'text-muted-foreground/60',
        )}
      >
        {label}
      </span>
    </button>
  )
}

export function TractFilterPanel({
  tierFilter,
  typeFilter,
  onTierToggle,
  onTypeToggle,
  disabled = false,
}: TractFilterPanelProps) {
  return (
    <div className="space-y-3">
      {/* T-426: state the constraint before the rows, so the greyed-out controls read as
          "not yet" rather than "broken". */}
      {disabled && (
        <p
          role="note"
          className="rounded bg-muted/60 px-2 py-1.5 text-[0.625rem] leading-snug text-muted-foreground"
        >
          These filters apply to the tract layer. Zoom in until tracts appear to use them.
        </p>
      )}
      <section>
        <p className="mb-1 text-[0.5625rem] font-semibold uppercase tracking-wider text-muted-foreground">
          Opportunity Score Tier
        </p>
        <div className="space-y-0">
          {TIER_ROWS.map((row) => (
            <FilterRow
              key={row.id}
              label={row.label}
              color={row.color}
              checked={tierFilter.includes(row.id)}
              onToggle={() => onTierToggle(row.id)}
              disabled={disabled}
            />
          ))}
        </div>
      </section>

      <section>
        <p className="mb-1 text-[0.5625rem] font-semibold uppercase tracking-wider text-muted-foreground">
          Location Classification
        </p>
        <div className="space-y-0">
          {TYPE_ROWS.map((row) => (
            <FilterRow
              key={row.id}
              label={row.label}
              checked={typeFilter.includes(row.id)}
              onToggle={() => onTypeToggle(row.id)}
              disabled={disabled}
            />
          ))}
        </div>
      </section>
    </div>
  )
}
